"use client";

import React, { useState, useEffect, useRef } from 'react';
import { useClickOutsideHandler } from '@/hooks/useClickOutsideHandler';
import { X, MapPin, Tag } from '@/components/icons';
import { Maximize2, Minimize2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import useHeaderOffset from '@/hooks/useHeaderOffset';
import { useScrollLock } from '@/hooks/useScrollLock';
import { es } from 'date-fns/locale';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import CommentList from './CommentList';
import NoteActivityFeed from './NoteActivityFeed';
import { Session } from '@supabase/supabase-js';
import { incrementNoteViewCount, registerNoteView } from '@/lib/supabase/notes';
import { usePullToClose } from '@/hooks/usePullToClose';
import ImageViewer from './ImageViewer';
import { useContentSidebar } from '@/context/ContentSidebarContext';
import PromoVotingPanel from './PromoVotingPanel';
import PromoBuyerActions from './PromoBuyerActions';
import { ExternalLink, Receipt, Store } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { getReverseGeocodeCity } from '@/lib/georeverse';
import { AnimatePresence, motion } from 'framer-motion';
import { usePlayBeep } from '@/hooks/usePlayBeep';
import { useVibrate } from '@/hooks/useVibrate';

interface NoteSidebarProps {
  note: any;
  isOpen: boolean;
  onClose: () => void;
  session: Session | null;
}

export const NoteSidebar: React.FC<NoteSidebarProps> = ({ note, isOpen, onClose, session }) => {
  const playBeep = usePlayBeep();
  const vibrate = useVibrate();
  const [isClosing, setIsClosing] = useState(false);
  const [isMinimizing, setIsMinimizing] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { minimizedNotes, toggleNoteMinimized, clearMinimizedNote, setShowNoteClickOverlay } = useContentSidebar();
  const isMinimized = note?.id ? minimizedNotes[note.id] : false;

  const handleClose = React.useCallback(() => {
    vibrate('decrease');
    setShowNoteClickOverlay(false);
    setIsClosing(true);
    setTimeout(() => {
      if (note?.id) {
        clearMinimizedNote(note.id);
      }
      onClose();
      setIsClosing(false);
    }, 200);
  }, [onClose, note?.id, clearMinimizedNote, setShowNoteClickOverlay, vibrate]);

  const handleMinimize = React.useCallback(() => {
    playBeep('decrease');
    vibrate('decrease');
    setIsMinimizing(true);
    setTimeout(() => {
      if (note?.id) {
        toggleNoteMinimized(note.id);
      }
      setIsMinimizing(false);
    }, 200);
  }, [note?.id, toggleNoteMinimized, playBeep, vibrate]);

  const handleMaximize = React.useCallback(() => {
    if (note?.id && minimizedNotes[note.id]) {
      playBeep('add');
      vibrate('add');
      toggleNoteMinimized(note.id);
    }
  }, [note?.id, minimizedNotes, toggleNoteMinimized, playBeep, vibrate]);

  const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
  const [cityName, setCityName] = useState<string | null>(null);
  const [viewerImageUrls, setViewerImageUrls] = useState<string[] | null>(null);
  const [transportationType, setTransportationType] = useState<string>('car');
  const [isReady, setIsReady] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const { headerHeight, isDesktop, bottomNavHeight, recalculate } = useHeaderOffset();

  // Wait for dimensions to be calculated before showing sidebar
  useEffect(() => {
    if (!isReady && headerHeight > 0) {
      // Small delay to ensure all layout calculations are complete
      const timer = setTimeout(() => setIsReady(true), 50);
      return () => clearTimeout(timer);
    }
  }, [headerHeight, isReady]);

  // Sincronizar isReady cuando cambia la nota
  useEffect(() => {
    setIsReady(false);
    setTransportationType('car');
  }, [note?.id]);

  // Pull-to-close: ref al ScrollArea
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  useScrollLock((isOpen && !isMinimized) || isClosing || isMinimizing, false, scrollAreaRef);

  // Parse items safely (handle both JSON string and array)
  const parseItems = (items: any): any[] => {
    if (!items) {
      return [];
    }
    if (Array.isArray(items)) {
      return items;
    }
    if (typeof items === 'string') {
      try {
        const parsed = JSON.parse(items);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        console.error('[NoteSidebar] parseItems: JSON parse error:', e);
        return [];
      }
    }
    return [];
  };

  const handleImageClick = (url: string, allUrls?: string[]) => {
    const urls = allUrls || [url];
    setViewerImageUrls(urls);
    setSelectedImageIndex(urls.indexOf(url) >= 0 ? urls.indexOf(url) : 0);
    setSelectedImageUrl(url);
    setIsImageViewerOpen(true);
  };

  const handleCloseImageViewer = () => {
    setIsImageViewerOpen(false);
    setSelectedImageUrl(null);
    setViewerImageUrls(null);
    setSelectedImageIndex(0);
  };

  // Activar pull-to-close (solo en móvil)
  usePullToClose(scrollAreaRef, {
    onClose: handleClose,
    threshold: 80,
    enabled: isOpen && !isDesktop && !isImageViewerOpen
  });

  // Recalcular medidas cuando el sidebar se abre
  useEffect(() => {
    if (isOpen) {
      recalculate();
      // Recalcular también después de un pequeño delay para asegurar que el DOM esté actualizado
      const timeoutId = setTimeout(recalculate, 50);
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, recalculate]);

  // Click fuera para cerrar
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen && !isImageViewerOpen && sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
        const el = event.target as HTMLElement | null;
        if (!el || !el.isConnected) return; // Ignore if element is no longer in the DOM

        // Check if we are clicking on a portal (like Select, Popovers or Dialogs) or Leaflet map
        const isPortal = el.closest('[data-radix-portal]') || 
                        el.closest('[data-radix-popper-content-wrapper]') ||
                        el.closest('[role="dialog"]') ||
                        el.closest('[role="listbox"]') ||
                        el.closest('[toast-close]') ||
                        el.closest('[role="status"]') ||
                        el.closest('.leaflet-container') ||
                        el.closest('[data-radix-toast-announcer]');
        if (isPortal) return;

        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, isImageViewerOpen, handleClose]);

  // Cerrar con tecla ESC
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        event.preventDefault();
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleClose]);

  // Incrementar contador de vistas al abrir
  useEffect(() => {
    const handleViewRegistration = async () => {
      if (isOpen && note?.id) {
        try {
          await incrementNoteViewCount(note.id);
          if (session?.user?.id) {
            await registerNoteView(note.id, session.user.id);
          } else {
            await registerNoteView(note.id);
          }
        } catch (err) {
          console.error('[NoteSidebar] Failed to register view:', err);
        }
      }
    };
    handleViewRegistration();
  }, [isOpen, note?.id, session?.user?.id]);

  // Get city name from georeverse (location_display_name comes from publisher's location)
  useEffect(() => {
    const fetchCity = async () => {
      if (note?.latitude && note?.longitude) {
        try {
          const city = await getReverseGeocodeCity(note.latitude, note.longitude);
          setCityName(city);
        } catch (err) {
          console.error('[NoteSidebar] Error fetching city:', err);
          setCityName(null);
        }
      }
    };
    fetchCity();
  }, [note?.latitude, note?.longitude]);

  if (!note) return null;

  const createdAtDate = new Date(note.created_at);
  const updatedAtDate = note.updated_at ? new Date(note.updated_at) : null;
  const publishedString = format(createdAtDate, "dd/MM/yyyy 'a las' HH:mm", { locale: es });
  const dateDisplay = note.is_edited && updatedAtDate
    ? `${publishedString} (Actualizado a las ${format(updatedAtDate, 'HH:mm:ss', { locale: es })})`
    : publishedString;

  // Get image URLs
  const maybeAny = (note as any).image_urls || null;
  const urls: string[] = Array.isArray(maybeAny) && maybeAny.length > 0 ? maybeAny : (note.photo_url ? [note.photo_url] : []);

  // Compute if we should actually show the animation
  const shouldShow = isOpen && !isClosing && isReady;

  // Minimized view
  if (isMinimized) {
    // Stack note sidebar first (bottom offset = 0), then product sidebar if also minimized
    const minimizedNoteBottomOffset = !isDesktop ? bottomNavHeight : 0;
    return (
      <div
        className={cn(
          'fixed bottom-0 w-80 bg-background border rounded-t-lg shadow-lg z-90 max-md:fixed max-md:w-full max-md:left-0 max-md:right-0 max-md:rounded-none',
          'transition-all duration-300',
          isClosing && 'animate-out slide-out-to-bottom duration-300'
        )}
        style={{ 
          right: isDesktop ? 'auto' : undefined, 
          bottom: !isDesktop ? `${minimizedNoteBottomOffset}px` : undefined,
        }}
      >
        <div
          role="button"
          onClick={handleMaximize}
          className="w-full p-3 flex items-center justify-between transition-colors rounded-t-lg max-md:rounded-none hover:bg-muted/50 cursor-pointer"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-muted/50 flex-shrink-0" />
            <span className="font-medium truncate">{note.title || 'Nota'}</span>
          </div>
          <div
            role="button"
            className="h-6 w-6 hover:bg-primary/20 rounded flex items-center justify-center transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              handleClose();
            }}
          >
            <X className="w-4 h-4" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <AnimatePresence>
      {(isOpen || isClosing) && (
      <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 bg-transparent z-[9997]"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) {
            e.stopPropagation();
            handleClose();
          }
        }}
      />

      {/* Sidebar */}
      <motion.div
        ref={sidebarRef}
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ duration: 0.2 }}
        className={cn(
          'fixed right-0 w-full lg:w-[400px] bg-background shadow-2xl z-[9998] flex flex-col overflow-hidden',
          'max-w-full'
        )}
        style={{
          top: isDesktop ? `${headerHeight}px` : '0',
          bottom: !isDesktop ? 0 : undefined,
          height: isDesktop ? `calc(100vh - ${headerHeight}px)` : '100dvh',
        }}
      >
      {/* Safe Area Spacer for Mobile */}
      {!isDesktop && <div className="h-[env(safe-area-inset-top,0px)] bg-primary w-full shrink-0" />}
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b shrink-0 bg-primary text-primary-foreground min-h-[64px]">
        <h2 className="text-lg font-semibold truncate min-w-0 text-primary-foreground">
          {note.title || 'Nota'}
        </h2>
        <div className="flex items-center gap-2 flex-shrink-0">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground"
                  onClick={() => {
                      playBeep('add');
                      vibrate('add');
                    onClose();
                    // Navegación interna con react-router
                    navigate(`/notas/${note.id}`);
                    window.dispatchEvent(new CustomEvent('note-sidebar-maximize', { detail: { noteId: note.id } }));
                  }}
                >
                  <Maximize2 className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent variant="sidebar">
                <p>Pantalla completa</p>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground"
                  onClick={(e) => { e.stopPropagation(); handleMinimize(); }}
                >
                  <Minimize2 className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent variant="sidebar">
                <p>Minimizar</p>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
                <TooltipTrigger asChild>
                <button
                  onClick={(e) => { e.stopPropagation(); handleClose(); }}
                  className="p-1 hover:bg-white/10 text-white rounded-lg transition-colors hover:text-white"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </TooltipTrigger>
              <TooltipContent variant="sidebar">
                <p>Cerrar</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {/* Content */}
      <ScrollArea ref={scrollAreaRef} className="flex-1 overflow-x-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 space-y-6 max-w-full" onClick={(e) => e.stopPropagation()}>
          {/* Publisher Block - Estética de OrderPage */}
          <div className="bg-card rounded-xl border border-border/50 p-3 shadow-sm">
            <div className="flex items-start gap-2.5">
              <Link to={`/${note.profiles?.username}`}>
                <Avatar className="h-10 w-10 flex-shrink-0">
                  <AvatarImage src={note.profiles?.avatar_url || undefined} alt={note.profiles?.display_name || note.profiles?.username || 'Usuario'} />
                  <AvatarFallback>
                    {note.profiles?.display_name ? note.profiles.display_name.charAt(0).toUpperCase() : (note.profiles?.username ? note.profiles.username.charAt(0).toUpperCase() : 'U')}
                  </AvatarFallback>
                </Avatar>
              </Link>
              <div className="flex-1 min-w-0">
                <Link to={`/${note.profiles?.username}`} className="font-medium text-sm hover:underline block truncate">
                  {note.profiles?.display_name || note.profiles?.username || 'Usuario'}
                </Link>
                <Link to={`/${note.profiles?.username}`} className="text-xs text-muted-foreground hover:underline block truncate">
                  minibid.org/{note.profiles?.username}
                </Link>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="text-xs text-muted-foreground mt-0.5 cursor-help hover:text-foreground transition-colors">
                        {format(createdAtDate, 'dd/MM/yyyy', { locale: es })}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="text-xs">
                      <div className="space-y-0.5 text-xs">
                        <p>Publicado el {format(createdAtDate, "d 'de' MMMM 'a las' HH:mm", { locale: es })}</p>
                        {note.is_edited && updatedAtDate && (
                          <p>Actualizado a las {format(updatedAtDate, 'HH:mm', { locale: es })}</p>
                        )}
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          </div>

          {/* Contenido */}
          <div
            className="prose prose-sm max-w-none break-words leading-relaxed text-sm whitespace-pre-line overflow-wrap bg-card rounded-xl border border-border/50 p-3 shadow-sm"
            style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
            dangerouslySetInnerHTML={{ __html: note.content }}
          />

          {/* Promo Specific Info - Nueva estética tipo RequestRideSidebar */}
          {(note.category_name?.toLowerCase() === 'promo' || note.category_name?.toLowerCase() === 'promos' || note.category_id === '00000000-0000-0000-0000-000000000001') && (
            <div className="space-y-4 animate-in fade-in duration-500">
              {/* Tienda y ubicación */}
              <div className="space-y-2 bg-card rounded-xl border border-border/50 p-3 shadow-sm">
                <Label className="text-sm font-semibold mb-1 block">Tienda</Label>
                <div className="flex items-center gap-2 text-primary font-bold">
                  <Store className="w-4 h-4" />
                  <span>{note.store_name || 'Local'}</span>
                </div>
                {(note.vendor_location_display_name || note.location_display_name) && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground bg-background/50 p-2 rounded border border-primary/5 overflow-x-auto">
                    <MapPin className="w-3.5 h-3.5 text-primary/60" />
                    <span className="break-all whitespace-pre-line overflow-wrap break-words" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                      Publicado desde {cityName || 'tu ubicación'} ({note.vendor_location_display_name || note.location_display_name})
                    </span>
                  </div>
                )}
              </div>

              {/* Productos */}
              <div className="space-y-2 bg-card rounded-xl border border-border/50 p-3 shadow-sm">
                <Label className="text-sm font-semibold mb-1 block">Productos y Precios</Label>
                {(() => {
                  const parsedItems = parseItems(note.items);
                  return parsedItems.length > 0 ? (
                    <div className="grid gap-2">
                      {parsedItems.map((item: any, idx: number) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-muted/40 rounded-lg border border-border/50 text-sm">
                          <span className="font-medium break-all whitespace-pre-line overflow-wrap break-words" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{item.name}</span>
                          <span className="font-bold text-primary">{formatCurrency(item.price)}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-4 border border-dashed rounded-lg text-center bg-muted/5">
                      <p className="text-xs text-muted-foreground">No hay items individuales listados.</p>
                    </div>
                  );
                })()}
              </div>

              {/* Link externo y cupón */}
              {(note.external_url || note.coupon_code) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-card rounded-xl border border-border/50 p-3 shadow-sm">
                  {note.external_url && (
                    <a 
                      href={note.external_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 p-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-bold hover:opacity-90 transition-opacity"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Ver Oferta
                    </a>
                  )}
                  {note.coupon_code && (
                    <div className="flex flex-col items-center justify-center p-2 bg-yellow-400/10 border border-yellow-400/20 rounded-lg">
                      <span className="text-[10px] uppercase font-bold text-yellow-600">Cupón</span>
                      <span className="font-mono font-bold text-sm tracking-wider">{note.coupon_code}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Acciones de compra (bloque principal) */}
              <div className="space-y-4 bg-card rounded-xl border border-border/50 p-3 shadow-sm">
                {(() => {
                  const parsedItems = parseItems(note.items);
                  return parsedItems.length > 0 ? (
                    <PromoBuyerActions 
                      note={note}
                      userId={session?.user?.id}
                      transportationType={transportationType}
                      onTransportationTypeChange={setTransportationType}
                      hideTransportSelector={false}
                      onOrderCreated={(orderId) => {
                      }}
                    />
                  ) : null;
                })()}
                {(() => {
                  const parsedItems = parseItems(note.items);
                  return parsedItems.length === 0 ? (
                    <div className="p-4 border border-dashed rounded-lg text-center bg-muted/20">
                      <p className="text-xs text-muted-foreground">Esta promo no tiene productos listados para compra automática.</p>
                    </div>
                  ) : null;
                })()}
              </div>

              {/* Popularidad de la promo (inline y compacta) */}
              <div className="px-1 pt-1 opacity-80">
                <PromoVotingPanel 
                  compact
                  noteId={note.id}
                  userId={session?.user?.id}
                  upvotes={note.upvotes || 0}
                  downvotes={note.downvotes || 0}
                  hotnessScore={note.hotness_score || 0}
                  categoryName={note.category_name}
                  initialUserVote={note.user_vote ?? undefined}
                />
              </div>
            </div>
          )}

          {/* Galería de imágenes */}
          {urls.length > 0 && (
            <div className="bg-card rounded-xl border border-border/50 p-3 shadow-sm">
              <Carousel className="w-full">
                <CarouselContent>
                  {urls.map((imageUrl, idx) => (
                    <CarouselItem key={idx}>
                      <div className="w-full rounded-lg overflow-hidden">
                        <div className="relative w-full aspect-video">
                          <img
                            src={imageUrl}
                            alt={note.title}
                            className="absolute inset-0 w-full h-full object-cover object-center rounded-lg cursor-pointer active:scale-95 transition-transform"
                            onClick={() => handleImageClick(imageUrl, urls)}
                          />
                        </div>
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                {urls.length > 1 && (
                  <>
                    <CarouselPrevious />
                    <CarouselNext />
                  </>
                )}
              </Carousel>
            </div>
          )}

          {/* Votación para todas las notas */}
          <div className="px-1 pt-1 opacity-80">
            <PromoVotingPanel 
              compact
              noteId={note.id}
              userId={session?.user?.id}
              upvotes={note.upvotes || 0}
              downvotes={note.downvotes || 0}
              hotnessScore={note.hotness_score || 0}
              categoryName={note.category_name}
              initialUserVote={note.user_vote ?? undefined}
            />
          </div>

          {/* Comentarios */}
          {note.id && (
            <div className="bg-card rounded-xl border border-border/50 p-3 shadow-sm">
              <NoteActivityFeed
                noteId={note.id} 
                session={session} 
                noteAuthorId={note.user_id} 
                noteAuthorUsername={note.profiles?.username || 'Usuario Desconocido'} 
              />
            </div>
          )}
        </div>
      </ScrollArea>

      <ImageViewer
        imageUrl={viewerImageUrls ? null : selectedImageUrl}
        imageUrls={viewerImageUrls ?? (selectedImageUrl ? [selectedImageUrl] : undefined)}
        startIndex={selectedImageIndex}
        isOpen={isImageViewerOpen}
        onClose={handleCloseImageViewer}
      />
      </motion.div>
    </>
      )}
    </AnimatePresence>
  );
};
